import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import type {
  ContentListItemDTO,
  EventListItemDTO,
  NoticeDTO,
} from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { fmtDate, formatEventDate, isWithinDays } from "../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  EmptyState,
} from "../components/ui";

// 会員ステータスの見た目マップ (MyPage と同じトーン指針)
const MEMBER_STATUS_LABELS: Record<string, string> = {
  active: "有効",
  payment_failed: "決済失敗",
  suspended: "停止",
  cancelled: "解約",
  pending_activation: "仮登録",
};
const MEMBER_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  active: "success",
  payment_failed: "danger",
  suspended: "warning",
  cancelled: "neutral",
  pending_activation: "warning",
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: "記事",
  video: "動画",
  pdf: "PDF",
  external_link: "外部リンク",
};
const CONTENT_TYPE_ICON: Record<string, string> = {
  article: "📄",
  video: "🎥",
  pdf: "📑",
  external_link: "🔗",
};

export function HomePage() {
  const { me } = useAuth();
  const [events, setEvents] = useState<EventListItemDTO[]>([]);
  const [notices, setNotices] = useState<NoticeDTO[]>([]);
  const [contents, setContents] = useState<ContentListItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingSurveys, setPendingSurveys] = useState<
    { surveyId: string; eventId: string; title: string; eventTitle: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get<{ items: EventListItemDTO[] }>(
        "/events?status=active&perPage=5",
      ),
      api.get<{ items: NoticeDTO[] }>("/notices?perPage=5"),
      api.get<{ items: ContentListItemDTO[] }>("/contents?perPage=3"),
      api.get<{ items: typeof pendingSurveys }>("/surveys/pending"),
    ])
      .then(([e, n, c, s]) => {
        if (cancelled) return;
        if (e.status === "fulfilled") setEvents(e.value.items);
        if (n.status === "fulfilled") setNotices(n.value.items);
        if (c.status === "fulfilled") setContents(c.value.items);
        if (s.status === "fulfilled") setPendingSurveys(s.value.items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 申込済み × 未来開催 の先頭 1 件 (自分の次のイベント)
  const nextMyEvent = useMemo(() => {
    const now = Date.now();
    return (
      events
        .filter(
          (e) =>
            e.isAppliedByMe &&
            e.eventStartAt &&
            new Date(e.eventStartAt).getTime() > now,
        )
        .sort(
          (x, y) =>
            new Date(x.eventStartAt!).getTime() -
            new Date(y.eventStartAt!).getTime(),
        )[0] ?? null
    );
  }, [events]);

  const unreadCount = useMemo(
    () => notices.filter((n) => n.isRead === false).length,
    [notices],
  );

  const newestContent = contents[0] ?? null;

  const member = me?.member ?? null;
  const displayName = member?.displayName ?? me?.user.email ?? "ゲスト";
  const hasMember = Boolean(member);

  return (
    <div className="space-y-10">
      {/* ヒーローバナー (画面全幅) */}
      <section className="-mx-4 md:-mx-8 lg:-mx-12 relative z-10">
        <img
          src="/hero-banner.png"
          alt="令和の虎 経営塾 — YouTubeでは話さない！有名虎による成功への経営ノウハウ / 成功を掴み取れ！人脈形成も学びと同時実現"
          className="w-full h-auto block"
        />
        {hasMember && (
          <div className="mx-4 md:mx-8 lg:mx-12 -mt-5 relative z-10">
            <div className="inline-flex items-center gap-3 rounded-xl bg-white border border-black/10 px-5 py-3.5 shadow-md">
              <span className="text-xs text-tora-ink/50">会員番号</span>
              <span className="text-lg md:text-xl font-bold font-mono tracking-wider text-tora-ink">
                {member!.memberNo}
              </span>
              <StatusBadge
                tone={MEMBER_STATUS_TONE[member!.status] ?? "neutral"}
                bold
              >
                {MEMBER_STATUS_LABELS[member!.status] ?? member!.status}
              </StatusBadge>
            </div>
          </div>
        )}
      </section>

      {/* アンケート未回答告知 */}
      {pendingSurveys.length > 0 && (
        <section className="rounded-xl border-2 border-tora bg-tora/5 p-5 md:p-6">
          <div className="flex items-start gap-3">
            <span className="text-3xl shrink-0">📝</span>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-tora">アンケートにご協力ください</h3>
              <p className="text-sm text-tora-ink/70 mt-1">
                講義にご参加いただきありがとうございました。振り返りアンケートへのご回答をお願いいたします。
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {pendingSurveys.map((s) => (
                  <Link
                    key={s.surveyId}
                    to={`/events/${s.eventId}/survey`}
                    className="inline-flex items-center gap-2 rounded-lg bg-tora text-white px-4 py-2.5 text-sm font-bold hover:bg-tora-accent transition-colors w-fit"
                  >
                    {s.eventTitle} の振り返りアンケートに回答する →
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 直近のイベント (左 60%) + お知らせ (右 40%) — 参考画像準拠 */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <section className="card p-6 md:p-7 md:col-span-3">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-2xl" aria-hidden="true">🗓</span>
            <h3 className="text-xl md:text-2xl font-bold text-tora">直近のイベント</h3>
            <Link
              to="/events"
              className="ml-auto text-sm text-tora font-semibold hover:underline"
            >
              すべて見る &gt;
            </Link>
          </div>
          {loading ? (
            <Loading />
          ) : events.length === 0 ? (
            <EmptyState
              icon="🗓"
              title="公開中のイベントはありません"
            />
          ) : (
            <>
            {/* 先頭 1 件を大きくフィーチャー (参考画像準拠) */}
            {events.slice(0, 1).map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                className="block group"
              >
                <div className="flex flex-col md:flex-row gap-5">
                  {/* 大サムネ (左 40%) */}
                  {uploadsUrl(e.thumbnailKey) ? (
                    <img
                      src={uploadsUrl(e.thumbnailKey)!}
                      alt=""
                      className="w-full md:w-[45%] aspect-[4/3] object-contain rounded-lg border border-black/10 bg-black/5"
                    />
                  ) : (
                    <div className="w-full md:w-[45%] aspect-[4/3] rounded-lg border border-black/10 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-5xl">
                      🗓
                    </div>
                  )}
                  {/* 詳細 (右 60%) */}
                  <div className="flex-1 flex flex-col">
                    {e.isAppliedByMe && (
                      <StatusBadge tone="accent" bold className="self-start mb-2">
                        申込済み
                      </StatusBadge>
                    )}
                    <h4 className="text-xl md:text-2xl font-bold text-tora-ink leading-snug group-hover:text-tora transition-colors">
                      {e.title}
                    </h4>
                    {e.summary && (
                      <p className="text-sm text-tora-ink/60 mt-2 line-clamp-2">{e.summary}</p>
                    )}
                    <div className="mt-auto pt-4 space-y-1.5 text-sm text-tora-ink/70">
                      {e.eventStartAt && (
                        <p className="flex items-center gap-2">
                          <span className="text-base">📅</span>
                          {(() => {
                            const { date, time } = formatEventDate(e.eventStartAt);
                            return `${date} ${time}`;
                          })()}
                        </p>
                      )}
                      {e.venueName && (
                        <p className="flex items-center gap-2">
                          <span className="text-base">📍</span>
                          {e.venueName}
                        </p>
                      )}
                      <p className="flex items-center gap-2">
                        <span className="text-base">🎫</span>
                        {e.participationMode === "online"
                          ? "オンライン"
                          : e.participationMode === "hybrid"
                            ? "来場・オンライン 両方可"
                            : "来場"}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            <Link
              to="/events"
              className="mt-6 flex items-center justify-center gap-2 rounded-xl border-2 border-tora text-tora font-bold text-base py-3.5 hover:bg-tora/5 transition-colors"
            >
              詳細を見る <span className="text-lg">&gt;</span>
            </Link>
            </>
          )}
        </section>

        <section className="card p-6 md:p-7 md:col-span-2">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-2xl" aria-hidden="true">📢</span>
            <h3 className="text-xl md:text-2xl font-bold text-tora-ink">お知らせ</h3>
            <Link
              to="/notices"
              className="ml-auto text-sm text-tora font-semibold hover:underline"
            >
              すべて見る &gt;
            </Link>
          </div>
          {loading ? (
            <Loading />
          ) : notices.length === 0 ? (
            <EmptyState icon="📢" title="お知らせはありません" />
          ) : (
            <div>
              {notices.slice(0, 5).map((n, i) => {
                const isNew = isWithinDays(n.publishedAt, 7);
                const thumb = uploadsUrl(n.thumbnailKey);
                return (
                  <Link
                    key={n.id}
                    to={`/notices/${n.id}`}
                    className={`group flex items-center gap-3 py-4 hover:bg-black/[0.02] transition-colors ${
                      i > 0 ? "border-t border-black/5" : ""
                    }`}
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="shrink-0 w-14 h-14 rounded-lg object-cover border border-black/10 bg-black/5"
                      />
                    ) : (
                      <div className="shrink-0 w-14 h-14 rounded-lg border border-black/10 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xl">
                        📢
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {n.publishedAt && (
                          <span className="text-xs text-tora-ink/50 tabular-nums shrink-0">
                            {fmtDate(n.publishedAt)}
                          </span>
                        )}
                        {isNew && (
                          <span className="shrink-0 rounded-md bg-tora text-white text-[10px] font-bold px-2 py-0.5">
                            NEW
                          </span>
                        )}
                      </div>
                      <span className="block text-sm font-bold text-tora-ink group-hover:text-tora truncate mt-0.5">
                        {n.title}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <Link
            to="/notices"
            className="mt-5 flex items-center justify-center gap-2 rounded-xl border-2 border-tora text-tora font-bold text-base py-3.5 hover:bg-tora/5 transition-colors"
          >
            お知らせ一覧へ <span className="text-lg">&gt;</span>
          </Link>
        </section>
      </div>
    </div>
  );
}
